import React from 'react';
import useTranslation from 'next-translate/useTranslation';
import { useFormContext } from 'react-hook-form';
import { styled } from '@linaria/react';
import { colors, FormControl, FormLabel, Input, Stack, Text } from '@spaceshipapp/cambridge';

const SIZE_MAX_LIMIT = 120;
const WEIGHT_MAX_LIMIT = 30;

interface Props {
  length: number;
  width: number;
  height: number;
  weight: number;
}

const DimensionsForm: React.FC<Props> = ({ length, width, height, weight }) => {
  const { t } = useTranslation('common');
  const {
    register,
    formState: { errors },
    watch,
  } = useFormContext();
  const SIZE_UNIT = t('Unit.cm');
  const WEIGHT_UNIT = t('Unit.kg');

  const isSizeMaxLimitReached = [
    watch('length') ?? length,
    watch('width') ?? width,
    watch('height') ?? height,
  ].some((value) => value > SIZE_MAX_LIMIT);

  const isWeightMaxLimitReached = (watch('weight') ?? weight) > WEIGHT_MAX_LIMIT;

  return (
    <Stack gap="lg" as="div">
      <Dimensions>
        <FormControl errorMessage={errors?.length?.message && t(errors?.length?.message)}>
          <FormLabel>{t('Parcel.labelLength')}</FormLabel>
          <Input
            type="number"
            {...register('length')}
            appendAddon={SIZE_UNIT}
            isError={!!errors?.length?.message}
            defaultValue={length}
          />
        </FormControl>
        <span>×</span>
        <FormControl errorMessage={errors?.width?.message && t(errors?.width?.message)}>
          <FormLabel>{t('Parcel.labelWidth')}</FormLabel>
          <Input
            type="number"
            {...register('width')}
            appendAddon={SIZE_UNIT}
            isError={!!errors?.width?.message}
            defaultValue={width}
          />
        </FormControl>
        <span>×</span>
        <FormControl errorMessage={errors?.height?.message && t(errors?.height?.message)}>
          <FormLabel>{t('Parcel.labelHeight')}</FormLabel>
          <Input
            type="number"
            {...register('height')}
            appendAddon={SIZE_UNIT}
            isError={!!errors?.height?.message}
            defaultValue={height}
          />
        </FormControl>
      </Dimensions>
      <FormControl errorMessage={errors?.weight?.message && t(errors?.weight?.message)}>
        <FormLabel htmlFor="weight">{t('Parcel.labelWeight')}</FormLabel>
        <Input
          type="number"
          step="0.1"
          {...register('weight')}
          appendAddon={WEIGHT_UNIT}
          isError={!!errors?.weight?.message}
          defaultValue={weight}
        />
      </FormControl>

      {isSizeMaxLimitReached && (
        <WarningText size="sm">
          {t('Parcel.warningMaxSizeLimit', { limit: SIZE_MAX_LIMIT })}
        </WarningText>
      )}

      {isWeightMaxLimitReached && (
        <WarningText size="sm">
          {t('Parcel.warningMaxWeightLimit', { limit: WEIGHT_MAX_LIMIT })}
        </WarningText>
      )}
    </Stack>
  );
};

const Dimensions = styled.div`
  display: flex;
  align-items: flex-start;

  > *:not(:first-child) {
    margin-left: 0.5rem;
  }

  > span {
    padding-top: 0.8rem;
    padding-bottom: 0.8rem;
  }
`;

const WarningText = styled(Text)`
  color: ${colors.highlightPrimary};
`;

export default DimensionsForm;
